import { useState } from "react";
import { Loader2, Search, ShieldCheck, Calendar } from "lucide-react";
import api from "../lib/api";
import Seo from "../components/Seo";

/**
 * /:lang/consultations  —  Email-anchored consultation history.
 *
 * Since the paid flow does not require login, we look up past consultations by the
 * email the visitor used at checkout. (Email is the natural identifier on the receipt.)
 */
const ConsultationHistory = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState(null);

  const fetchHistory = async (e) => {
    e?.preventDefault();
    if (!email || !email.includes("@")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/consultation/history", { email });
      setRows(data.consultations || []);
    } catch {
      setRows([]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-noir min-h-screen pt-28">
      <Seo
        title="Consultation History"
        description="Review your paid consultations with the Senior Gemologist of OPIKJEWELS."
        path="/consultations"
        noIndex
      />
      <section className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16 py-12">
        <div className="mb-10">
          <div className="inline-flex items-center gap-1.5 bg-gold/15 border border-gold/40 text-gold font-accent text-[10px] tracking-[0.24em] uppercase px-3 py-1.5 mb-4">
            <ShieldCheck size={11} /> Professional Consultations
          </div>
          <h1 className="font-heading text-4xl md:text-5xl text-ivory mb-2">Your Reading History</h1>
          <p className="text-white/50 text-sm">Enter the email you used at checkout to view all past readings.</p>
        </div>

        <form onSubmit={fetchHistory} className="flex flex-col md:flex-row gap-3 mb-10" data-testid="consult-history-form">
          <input
            data-testid="consult-history-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
            className="flex-1 bg-transparent border border-white/20 px-4 py-3 text-white focus:outline-none focus:border-gold"
          />
          <button
            data-testid="consult-history-submit"
            type="submit"
            disabled={busy}
            className="flex items-center justify-center gap-2 bg-gold text-black font-accent text-[11px] tracking-[0.28em] uppercase px-6 py-3 hover:bg-ivory disabled:opacity-50"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Search size={13} />}
            View
          </button>
        </form>

        {rows !== null && rows.length === 0 && (
          <div data-testid="consult-history-empty" className="border border-white/10 bg-black/40 p-8 text-center text-white/50 text-sm">
            No consultations found for this email yet.
          </div>
        )}

        {rows !== null && rows.length > 0 && (
          <div className="space-y-5" data-testid="consult-history-list">
            {rows.map((r) => (
              <article key={r.id} className="border border-white/10 bg-black/40 p-6 md:p-7">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <span className="font-accent text-[9px] tracking-[0.24em] uppercase text-gold">
                    {r.status === "answered" ? "Answered" : "Unanswered"}
                  </span>
                  <span className="font-accent text-[10px] tracking-[0.2em] uppercase text-white/40 flex items-center gap-1">
                    <Calendar size={10} />
                    {(r.answered_at || r.created_at || "").slice(0, 10)}
                  </span>
                  <span className="font-accent text-[10px] tracking-[0.2em] uppercase text-white/40">
                    Rp {(r.amount_idr || 0).toLocaleString("id-ID")}
                  </span>
                </div>
                {r.question && (
                  <>
                    <div className="font-accent text-[10px] tracking-[0.22em] uppercase text-white/50 mb-1">Question</div>
                    <p className="text-white/80 text-sm mb-4">{r.question}</p>
                  </>
                )}
                {r.answer && (
                  <>
                    <div className="font-accent text-[10px] tracking-[0.22em] uppercase text-gold mb-1">Reading</div>
                    <p className="text-white/85 text-base leading-relaxed font-light italic border-l-2 border-gold/40 pl-4">
                      {r.answer}
                    </p>
                  </>
                )}
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default ConsultationHistory;
